<script lang="ts">

import Price from "../../../../common/components/ob1/Price.vue";
import {PropType} from "vue";
import {PrixDTO} from "../../../common/configurator/models/InsuranceCrossSellOptionWithUnknownDevice";
import {
    PriceDetailsCashbackType,
    PriceDetailsPopinDto
} from "../../../../common/components/custom/models/PriceDetailsPopinDto";

export default {
    name: "PriceDetailsContent",
    components: {
        Price,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        offerType: {
            type: String,
            required: true,
        },
        offerName: {
            type: String,
            required: true,
        },
        offerPrice: {
            type: Object as PropType<PrixDTO>,
            required: true,
        },
        priceDetails: {
            type: Object as PropType<PriceDetailsPopinDto>,
            required: true,
        },
    },
    computed: {
        modalId() {
            return `price-details-popin-${this.id}`;
        },
        tileFinalPrice() {
            return this.priceDetails ? this.priceDetails.finalPrice : this.offerPrice.price;
        },
        tileFinalPricePermanent() {
            return this.priceDetails && this.priceDetails.finalPricePermanent
                ? this.priceDetails.finalPricePermanent
                : this.offerPrice.initialPrice;
        },
        tileDuration() {
            if (this.priceDetails && this.priceDetails.finalPriceDuration) {
                return this.priceDetails.finalPriceDuration;
            }
            return this.offerPrice.duration;
        },
        showTileOdrPrice() {
            return Boolean(this.priceDetails && this.priceDetails.cashbacks.length > 0);
        },
        cashbackType(): PriceDetailsCashbackType | undefined {
            if (this.priceDetails) {
                if (this.priceDetails.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.CHEAT_CODE
                )) {
                    return PriceDetailsCashbackType.CHEAT_CODE;
                } else if (this.priceDetails.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.SAFER_PHONE
                )) {
                    return PriceDetailsCashbackType.SAFER_PHONE;
                } else if (this.priceDetails.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.OTHER
                )) {
                    return PriceDetailsCashbackType.OTHER;
                }
            }
            return undefined;
        },
        showTileEndOfPromotionPrice() {
            return Boolean(this.priceDetails
                && this.priceDetails.hasDifferentNonPermanentDurations
                && !this.priceDetails.finalPriceDuration);
        }
    },
};
</script>

<template>
    <div>
        <!--/* PRIX DE BASE */-->
        <div class="d-flex justify-content-between align-items-center mb-1">
            <div class="col-7">
                <span class="visually-hidden">{{ offerType }} {{ offerName }}</span>
                <p class="pt-xl-1 py-0 mb-0 fw-bold" aria-hidden="true">
                    {{ offerType }}
                </p>
                <h2 class="h1 py-0 mb-0" aria-hidden="true">
                    {{ offerName }}
                </h2>
            </div>
            <div v-if="priceDetails.basePrice" class="ps-1 text-end">
                <Price
                    :price="priceDetails.basePrice"
                    :show-by-month="true"
                    :is-cents-format="false"
                    :is-compact="true"
                    :custom="'text-dark text-decoration-line-through'"
                />
            </div>
        </div>

        <!--/* REMISES */-->
        <div
            v-for="(discount, index) in priceDetails.discounts"
            :key="`${modalId}-discount-${index}`"
            class="ob1-inline-promise ob1-inline-promise-warning mb-1"
        >
            <div class="d-flex justify-content-between align-items-center w-100">
                <div class="col-7 lead">
                    {{ discount.label }}
                </div>
                <div class="text-end ps-1">
                    <Price
                        :price="discount.amount"
                        :show-by-month="true"
                        :is-cents-format="false"
                        :is-mini="true"
                        :price-details="discount.durationLabel || ''"
                    />
                </div>
            </div>
        </div>

        <!--/* Cashbacks (offres de remboursement) */-->
        <div
            v-if="priceDetails.cashbacks && priceDetails.cashbacks.length > 0"
            class="mb-1"
        >
            <hr
                v-if="priceDetails.discounts && priceDetails.discounts.length > 0"
                class="dotted-separator my-1"
                aria-hidden="true"
            >

            <div
                v-if="priceDetails.discounts && priceDetails.discounts.length > 0"
                class="d-flex justify-content-between align-items-center pb-2"
            >
                <span class="lead fw-bold">{{ priceDetails.totalBeforeRefundLabel }}</span>
                <Price
                    :price="priceDetails.totalBeforeRefund"
                    :show-by-month="true"
                    :is-cents-format="false"
                    :is-compact="true"
                    :custom="'text-dark'"
                />
            </div>

            <div
                v-for="(cashback, index) in priceDetails.cashbacks"
                :key="`${modalId}-cashback-${index}`"
                class="ob1-inline-promise ob1-inline-promise-warning mb-1"
            >
                <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="col-7">
                        <p v-msh-html="cashback.label" class="lead mb-0" />
                        <a
                            :href="cashback.url"
                            target="_blank"
                            class="ob1-link underline-link"
                            :aria-label="cashback.ctaLabel + ' (nouvelle fenêtre)'"
                        >{{ cashback.ctaLabel }}</a>
                    </div>
                    <div class="text-end ps-1">
                        <Price
                            :price="cashback.amount"
                            :show-by-month="true"
                            :is-cents-format="false"
                            :is-mini="true"
                            :price-details="cashback.durationLabel + '<br>' + cashback.invoiceMention"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!--/* PRIX FINAL */-->
        <div>
            <hr class="separator separator--gray4 my-1" aria-hidden="true">
            <div class="d-flex justify-content-between align-items-start">
                <p v-msh-html="priceDetails.finalPriceLabel" class="lead fw-bold mb-0" />

                <div class="text-end">
                    <Price
                        :price="priceDetails.finalPrice"
                        :initial-price="tileFinalPricePermanent"
                        :price-details="offerPrice.priceDetails || ''"
                        :duration="tileDuration"
                        :is-cents-format="false"
                        :show-from="offerPrice.showFrom"
                        :show-by-month="offerPrice.showByMonth"
                        :show-promo-duration="offerPrice.showPromoDuration"
                        :show-end-of-promotion-price="offerPrice.showEndOfPromotionPrice"
                        :show-children-promotion-price="offerPrice.showChildrenPromotionPrice"
                        :cashback-type="cashbackType"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

.ob1-inline-promise {
    background-color: transparent;
    padding-right: 0;
}

.dotted-separator{
    border-top: 1px dotted #ccc;
}

</style>
